import { useRef, useState } from "react";
import type { PopoverActions } from "@mui/material/Popover";
import {
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    FormControl,
    IconButton,
    Collapse
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const getRoleKey = (role: any) => {
    const roleName = role?.name ?? role?.Name ?? '';
    return roleName.charAt(0).toLowerCase() + roleName.slice(1);
};

const getPermissions = (roles: any[]) => roles.map((role: any) => ({
    roleID: role?.id ?? role?.roleID ?? role?.RoleID,
    key: getRoleKey(role),
    label: role?.name ?? role?.Name ?? ''
}));

const isAdminPermission = (permission: any) => permission?.label?.toLowerCase() === 'admin';

const buildRoleMappings = (client: any, permissions: any[]) =>
    client.selected
        ? permissions
            .filter((perm) => client?.[perm.key])
            .map((perm) => ({ roleID: perm.roleID }))
        : [];

const withRoleMappings = (client: any, permissions: any[]) => ({
    ...client,
    customerClientRoleMappings: buildRoleMappings(client, permissions)
});

const ClientPermissionsAccordion = ({ row, roles, onChange }: any) => {
    const permissions = getPermissions(roles || []);
    const menuAction = useRef<PopoverActions>(null);
    // Set of clientIDs currently expanded, so any number of accordions can stay open together
    const [expandedClients, setExpandedClients] = useState<Set<any>>(new Set());

    const toggleExpanded = (clientID: any) => {
        setExpandedClients((prev) => {
            const next = new Set(prev);
            next.has(clientID) ? next.delete(clientID) : next.add(clientID);
            return next;
        });
    };

    const emitChange = (updatedClientUserMaps: any[]) => {
        const mappedClientUserMaps = updatedClientUserMaps.map((client) => withRoleMappings(client, permissions));
        const areClientsModified = JSON.stringify(row.clientUserMaps) !== JSON.stringify(mappedClientUserMaps);
        const hasAdmin = mappedClientUserMaps.some((client) =>
            permissions.some((perm) => isAdminPermission(perm) && client[perm.key])
        );
        onChange(row.id, mappedClientUserMaps, areClientsModified, hasAdmin);
    };

    const resetPermissions = (client: any, selected: boolean) =>
        permissions.reduce((acc, perm) => ({ ...acc, [perm.key]: selected ? client[perm.key] : false }), {});

    const handleSelectChange = (event: any) => {
        const selectedClientIds = event.target.value;
        const updatedClientUserMaps = row.clientUserMaps.map((client: any) => {
            const isSelected = selectedClientIds.includes(client.clientID);
            return { ...client, selected: isSelected, ...resetPermissions(client, isSelected) };
        });
        emitChange(updatedClientUserMaps);
    };

    const handleParentToggle = (clientID: any) => {
        const updatedClientUserMaps = row.clientUserMaps.map((client: any) => {
            if (client.clientID !== clientID) return client;
            const selected = !client.selected;
            return { ...client, selected, ...resetPermissions(client, selected) };
        });
        emitChange(updatedClientUserMaps);
    };

    const handlePermissionChange = (clientID: any, key: string) => {
        const updatedClientUserMaps = row.clientUserMaps.map((client: any) =>
            client.clientID === clientID ? { ...client, [key]: !client[key] } : client
        );
        emitChange(updatedClientUserMaps);
    };

    return (
        <FormControl>
            <Select
                multiple
                size="small"
                value={row.clientUserMaps.filter((client: any) => client.selected).map((client: any) => client.clientID)}
                onChange={handleSelectChange}
                renderValue={(selected: any) =>
                    row.clientUserMaps.filter((client: any) => selected.includes(client.clientID)).map((client: any) => client.clientName).join(", ")
                }
                MenuProps={{
                    action: menuAction,
                    disableScrollLock: true,
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    MenuListProps: {
                        sx: {
                            maxHeight: "min(300px, calc(100vh - 32px))",
                            minHeight: 0,
                            flex: "1 1 auto",
                            overflowY: "scroll",
                            overflowX: "hidden",
                            scrollbarGutter: "stable",
                            overscrollBehaviorY: "contain",
                            touchAction: "pan-y",
                            boxSizing: "border-box"
                        }
                    },
                    PaperProps: {
                        style: {
                            maxHeight: "min(300px, calc(100vh - 32px))",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden"
                        }
                    }
                }}
                sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "300px"
                }}
            >
                {row.clientUserMaps.map((client: any) => (
                    <div key={client.clientID}>
                        <MenuItem
                            style={{ fontSize: '12px' }}
                            sx={{ display: 'flex', justifyContent: 'space-between' }}
                            value={client.clientID}
                            onClick={() => handleParentToggle(client.clientID)}
                        >
                            <Checkbox checked={client.selected} />
                            <ListItemText primary={client.clientName} />
                            {client.selected && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpanded(client.clientID);
                                    }}
                                >
                                    <ExpandMoreIcon
                                        style={{
                                            transform: expandedClients.has(client.clientID) ? "rotate(180deg)" : "rotate(0deg)",
                                            transition: "transform 0.2s"
                                        }}
                                    />
                                </IconButton>
                            )}
                        </MenuItem>
                        {client.selected && (
                            <Collapse
                                in={expandedClients.has(client.clientID)}
                                onEntered={() => menuAction.current?.updatePosition()}
                                onExited={() => menuAction.current?.updatePosition()}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div style={{ paddingLeft: "40px" }}>
                                    {permissions.map((perm) => (
                                        <MenuItem key={perm.key} style={{ fontSize: '12px', padding: '0' }} disableRipple>
                                            <Checkbox
                                                checked={client[perm.key] || false}
                                                onChange={() => handlePermissionChange(client.clientID, perm.key)}
                                            />
                                            <ListItemText primary={perm.label} />
                                        </MenuItem>
                                    ))}
                                </div>
                            </Collapse>
                        )}
                    </div>
                ))}
            </Select>
        </FormControl>
    );
};

export default ClientPermissionsAccordion;
